import CustomError from "./customError";

export interface ValidateFilesProps {
  files: File[];
  allowedTypes: readonly string[];
  maxSize: number;
}

export default class ValidateFiles {
  readonly _files: File[];

  private readonly _allowedTypes: readonly string[];

  private readonly _maxSize: number;

  constructor({ files, allowedTypes, maxSize }: ValidateFilesProps) {
    this._files = files;
    this._allowedTypes = allowedTypes;
    this._maxSize = maxSize;
  }

  validate(): void {
    this._files.forEach((file) => {
      this._validateSingleFile(file);
    });
  }

  validateSize(): void {
    this._files.forEach((file) => {
      this._isValidFileSize(file.size);
    });
  }

  validateType(): void {
    this._files.forEach((file) => {
      this._isValidFileType(file.name);
    });
  }

  private _validateSingleFile(file: File): void {
    this._isValidFileSize(file.size);
    this._isValidFileType(file.name);
  }

  private _getFileExtension(fileName: string): string {
    const fileNameParts = fileName.split(".");
    return fileNameParts[fileNameParts.length - 1].toLowerCase();
  }

  private _getFileSizeInMB(sizeInBytes: number): number {
    return sizeInBytes / (1000 * 1000);
  }

  private _isValidFileType(fileName: string) {
    const fileExtension = this._getFileExtension(fileName);
    const isValid = this._allowedTypes.includes(fileExtension);

    if (!isValid) {
      throw new CustomError({
        id: "ws_invalid_file_type",
        defaultMessage: "Invalid file type.",
      });
    }
  }

  private _isValidFileSize(fileSize: number) {
    const sizeInMB = this._getFileSizeInMB(fileSize);
    const isValid = sizeInMB <= this._maxSize;
    if (!isValid) {
      throw new CustomError({
        id: "ws_file_too_large",
        defaultMessage: "File is too large.",
      });
    }
  }

  get maxSize(): number {
    return this._maxSize;
  }

  get files(): File[] {
    return this._files;
  }

  get allowedTypes(): readonly string[] {
    return this._allowedTypes;
  }
}
